import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import io from "socket.io-client";
import { FORM_SCHEMA } from "./formSchema";

const SOCKET_ORIGIN =
  process.env.NODE_ENV === "development" ? "http://localhost:5000" : window.location.origin;

function parseBotPayload(text) {
  if (typeof text !== "string") return null;
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function proposalsFromPayload(payload) {
  const proposals = payload?.field_proposals || payload?.field_updates || [];
  return Array.isArray(proposals) ? proposals : [];
}

function recordFromPayload(payload) {
  const record = payload?.record || payload?.answers;
  return record && typeof record === "object" && !Array.isArray(record) ? record : null;
}

function recordVersionsFromPayload(payload) {
  const versions = payload?.current_record_versions;
  return versions && typeof versions === "object" && !Array.isArray(versions)
    ? versions
    : null;
}

const EMPTY_UI_STATE = {
  edits: {},
  review_status_events: {},
  mismatch_action_events: {},
  verification_stage: null,
  final_edits: {},
  final_record: null,
  submitted: false,
  updated_at: null,
};

function objectOrEmpty(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function normalizeUiState(value) {
  const state = objectOrEmpty(value);
  return {
    edits: objectOrEmpty(state.edits),
    review_status_events: objectOrEmpty(state.review_status_events),
    mismatch_action_events: objectOrEmpty(state.mismatch_action_events),
    verification_stage: state.verification_stage && typeof state.verification_stage === "object"
      ? state.verification_stage
      : null,
    final_edits: objectOrEmpty(state.final_edits),
    final_record: state.final_record && typeof state.final_record === "object" && !Array.isArray(state.final_record)
      ? state.final_record
      : null,
    submitted: Boolean(state.submitted),
    updated_at: state.updated_at || null,
  };
}

export function mergeUiStates(currentValue, incomingValue) {
  const current = normalizeUiState(currentValue);
  const incoming = normalizeUiState(incomingValue);
  return {
    edits: { ...current.edits, ...incoming.edits },
    review_status_events: {
      ...current.review_status_events,
      ...incoming.review_status_events,
    },
    mismatch_action_events: {
      ...current.mismatch_action_events,
      ...incoming.mismatch_action_events,
    },
    verification_stage: incoming.verification_stage || current.verification_stage,
    final_edits: { ...current.final_edits, ...incoming.final_edits },
    final_record: incoming.final_record || current.final_record,
    submitted: current.submitted || incoming.submitted,
    updated_at: [current.updated_at, incoming.updated_at]
      .filter(Boolean)
      .sort()
      .pop() || null,
  };
}

function payloadIsComplete(payload) {
  return Boolean(payload?.complete || payload?.type === "completion");
}

function schemaParentIndex(schema) {
  const index = {};
  for (const item of schema || []) {
    if (item.structure_type === "group") {
      for (const field of item.fields || []) {
        index[field.id] = { parent: item.id, kind: "group" };
      }
    }
  }
  return index;
}

const PARENT_INDEX = schemaParentIndex(FORM_SCHEMA);

export function updateRecord(record, fieldId, value) {
  const repeatMatch = String(fieldId).match(/^([A-Za-z0-9_-]+)\[(\d+)]\.([A-Za-z0-9_-]+)$/);
  if (repeatMatch) {
    return { ...record, [fieldId]: value };
  }

  const parent = PARENT_INDEX[fieldId];
  if (parent?.kind === "group") {
    return {
      ...record,
      [parent.parent]: {
        ...(record[parent.parent] || {}),
        [fieldId]: value,
      },
    };
  }

  return { ...record, [fieldId]: value };
}

export function applyUiStateToRecord(baseRecord, uiState) {
  let next = objectOrEmpty(baseRecord);
  const state = normalizeUiState(uiState);

  for (const edit of Object.values(state.edits)) {
    if (edit?.field_id) next = updateRecord(next, edit.field_id, edit.value);
  }

  // Final holistic corrections are applied last. Primary card responses remain
  // process evidence; edits are stored on their own append-only path.
  for (const edit of Object.values(state.final_edits)) {
    if (edit?.field_id) next = updateRecord(next, edit.field_id, edit.value);
  }

  if (state.final_record) next = { ...state.final_record };

  return next;
}

export function normalizeMessage(message) {
  const payload = message?.sender === "bot" ? parseBotPayload(message.text) : null;
  const text = payload?.message || payload?.text || message?.text || "";
  const messageId = message?.message_id || message?.id || `${Date.now()}-${Math.random()}`;
  return {
    kind: "msg",
    id: messageId,
    message_id: messageId,
    client_turn_id: message?.client_turn_id || payload?.client_turn_id || null,
    reply_to_turn_id: message?.reply_to_turn_id || payload?.reply_to_turn_id || null,
    sender: message?.sender === "participant" ? "participant" : "bot",
    text,
    raw: message,
    payload,
    complete: payloadIsComplete(payload),
  };
}

function normalizeEdit(update, index, messageKey) {
  const fieldId = update.field_id;
  return {
    kind: "edit",
    id: update.id || `${messageKey || "message"}:${fieldId || "field"}:${index}`,
    event_id: update.event_id || update.id,
    proposal_id: update.proposal_id || update.event_id || update.id,
    proposal_version: update.proposal_version || 1,
    record_version: update.record_version,
    current_record_version: update.current_record_version || update.record_version,
    field_complete: update.field_complete === true,
    field_id: fieldId,
    value: update.value,
    ai_proposed_value: update.ai_proposed_value !== undefined
      ? update.ai_proposed_value
      : update.value,
    source_evidence_id: update.source_evidence_id,
    source_evidence_version: update.source_evidence_version,
    source_fragment_ids: Array.isArray(update.source_fragment_ids)
      ? update.source_fragment_ids
      : [],
    previous_value: update.previous_value,
    source: update.source || "active",
    raw: update,
  };
}

export function itemsFromMessage(message, messageIndex = 0) {
  const item = normalizeMessage(message);
  const proposals = item.sender === "bot" ? proposalsFromPayload(item.payload) : [];
  const messageKey = message?.message_id || message?.id || message?.timestamp || `message-${messageIndex}`;
  const edits = proposals
    .filter((u) => u?.field_id)
    .map((update, index) => normalizeEdit(update, index, messageKey));
  return [...edits, item];
}

function stateFromHistory(messages) {
  let record = {};
  let recordVersions = {};
  let complete = false;
  const items = [];

  for (const [index, message] of messages.entries()) {
    const messageItems = itemsFromMessage(message, index);
    items.push(...messageItems);

    const messageItem = messageItems.find((it) => it.kind === "msg");
    const payload = messageItem?.payload;
    const payloadRecord = recordFromPayload(payload);
    if (payloadRecord) {
      record = payloadRecord;
    } else {
      const edits = messageItems.filter((it) => it.kind === "edit");
      record = edits.reduce((next, edit) => updateRecord(next, edit.field_id, edit.value), record);
    }
    const payloadRecordVersions = recordVersionsFromPayload(payload);
    if (payloadRecordVersions) recordVersions = payloadRecordVersions;

    if (payloadIsComplete(payload)) complete = true;
  }

  return { items, record, recordVersions, complete };
}

export function buildTelemetryRequest(eventOrEvents, { room, session, condition }) {
  const isBatch = Array.isArray(eventOrEvents);
  const events = isBatch ? eventOrEvents : [eventOrEvents];
  const keepalive = events.some((event) => Boolean(event?.keepalive));
  const telemetryEvents = events.map((event) => {
    const telemetryEvent = { ...(event || {}) };
    delete telemetryEvent.keepalive;
    return telemetryEvent;
  });
  return {
    keepalive,
    payload: {
      room_id: room,
      session,
      condition,
      ...(isBatch ? { events: telemetryEvents } : telemetryEvents[0]),
    },
  };
}

export function useStudySession({ room, session, condition }) {
  const socketRef = useRef(null);
  const [items, setItems] = useState([]);
  const [baseRecord, setBaseRecord] = useState({});
  const [recordVersions, setRecordVersions] = useState({});
  const [uiState, setUiState] = useState(EMPTY_UI_STATE);
  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState("connecting");
  const [error, setError] = useState(null);
  const [agentBusy, setAgentBusy] = useState(false);
  const [turnError, setTurnError] = useState(null);
  const [complete, setComplete] = useState(false);
  const uiStateRef = useRef(EMPTY_UI_STATE);

  const commitUiState = useCallback((incoming) => {
    const merged = mergeUiStates(uiStateRef.current, incoming);
    uiStateRef.current = merged;
    setUiState(merged);
    return merged;
  }, []);

  const record = useMemo(
    () => applyUiStateToRecord(baseRecord, uiState),
    [baseRecord, uiState]
  );

  const addMessage = useCallback((message) => {
    const messageItems = itemsFromMessage(message);
    setItems((current) => [...current, ...messageItems]);
    const messageItem = messageItems.find((it) => it.kind === "msg");
    if (messageItem?.sender === "bot") {
      setAgentBusy(false);
      setTurnError(null);
    }

    const payload = messageItem?.payload;
    const payloadRecord = recordFromPayload(payload);
    if (payloadRecord) {
      setBaseRecord(payloadRecord);
    } else {
      const edits = messageItems.filter((it) => it.kind === "edit");
      if (edits.length) {
        setBaseRecord((current) =>
          edits.reduce((next, edit) => updateRecord(next, edit.field_id, edit.value), current)
        );
      }
    }
    const payloadRecordVersions = recordVersionsFromPayload(payload);
    if (payloadRecordVersions) setRecordVersions(payloadRecordVersions);

    if (payloadIsComplete(payload)) setComplete(true);
  }, []);

  const applyUpdates = useCallback((updates = []) => {
    const edits = updates
      .filter((u) => u?.field_id)
      .map((update, index) => normalizeEdit(update, index, "field_updates"));
    if (!edits.length) return;
    setItems((current) => [...current, ...edits]);
    setBaseRecord((current) =>
      edits.reduce((next, edit) => updateRecord(next, edit.field_id, edit.value), current)
    );
    setRecordVersions((current) => edits.reduce((next, edit) => ({
      ...next,
      [edit.field_id]: edit.current_record_version,
    }), current));
  }, []);

  useEffect(() => {
    if (!room || !session) return undefined;

    let cancelled = false;

    fetch(`/api/study/state/${encodeURIComponent(room)}?session=${encodeURIComponent(session)}`)
      .then((response) => {
        if (!response.ok) throw new Error(`study state failed: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (!cancelled) commitUiState(data.ui_state);
      })
      .catch(() => {
        if (!cancelled) setStatus("state-error");
      });

    const socket = io(SOCKET_ORIGIN, {
      transports: ["websocket"],
      path: "/socket.io",
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      setStatus("connected");
      socket.emit("join", {
        room,
        type: "participant",
        client_mode: "study",
        study_token: session,
        condition,
      });
    });

    socket.on("disconnect", () => {
      setConnected(false);
      setAgentBusy(false);
      setStatus("disconnected");
    });

    socket.on("connect_error", () => {
      setError("connection");
      setStatus("connection-error");
    });

    socket.on("study_session_error", () => {
      setError("invalid-session");
      setStatus("invalid-session");
    });

    socket.on("message_history", ({ messages = [] }) => {
      const next = stateFromHistory(messages);
      setItems(next.items);
      setBaseRecord(next.record);
      setRecordVersions(next.recordVersions);
      setComplete(next.complete);
      setAgentBusy(false);
      setTurnError(null);
    });

    socket.on("new_message", addMessage);
    socket.on("field_updates", ({ updates = [] }) => applyUpdates(updates));
    socket.on("record_state", (data = {}) => {
      const nextRecord = data.record;
      if (nextRecord && typeof nextRecord === "object" && !Array.isArray(nextRecord)) {
        setBaseRecord(nextRecord);
      }
      const nextVersions = data.current_record_versions;
      if (nextVersions && typeof nextVersions === "object" && !Array.isArray(nextVersions)) {
        setRecordVersions(nextVersions);
      }
      if (data.complete) setComplete(true);
    });
    socket.on("bot_status_changed", (data = {}) => {
      if (data.processing || data.state === "thinking") {
        setAgentBusy(true);
        setTurnError(null);
        setStatus("thinking");
        return;
      }

      if (data.state === "error") {
        setAgentBusy(false);
        setTurnError(data.message || "Assistant could not respond. Please try again.");
        setStatus("connected");
        return;
      }

      setAgentBusy(false);
      if (data.active === false && /completed/i.test(data.message || "")) {
        setComplete(true);
        setStatus("completed");
        return;
      }
      setStatus("connected");
    });

    return () => {
      cancelled = true;
      socket.emit("leave", { room });
      socket.off();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [addMessage, applyUpdates, commitUiState, condition, room, session]);

  const sendMessage = useCallback((text, options = {}) => {
    if (!socketRef.current || !connected) return false;
    const participantMessage = buildParticipantMessagePayload(room, text, options);
    const clientTurnId = participantMessage.client_turn_id;
    setTurnError(null);
    setAgentBusy(true);
    setStatus("thinking");
    socketRef.current.emit("participant_message", participantMessage);
    return clientTurnId;
  }, [connected, room]);

  const sendDecision = useCallback((payload) => {
    if (!socketRef.current || !connected) return false;
    socketRef.current.emit("edit_decision", { room, ...payload });
    return true;
  }, [connected, room]);

  const postTelemetry = useCallback(async (eventOrEvents) => {
    const telemetryRequest = buildTelemetryRequest(eventOrEvents, {
      room,
      session,
      condition,
    });
    const response = await fetch("/api/study/telemetry", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: telemetryRequest.keepalive,
      body: JSON.stringify(telemetryRequest.payload),
    });
    if (!response.ok) throw new Error(`telemetry failed: ${response.status}`);
    return response.json();
  }, [condition, room, session]);

  const saveUiState = useCallback(async (patch) => {
    const request = {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        room_id: room,
        session,
        ...patch,
      }),
    };
    let lastError = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await fetch(`/api/study/state/${encodeURIComponent(room)}`, request);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const error = new Error(data.error || `study state failed: ${response.status}`);
          error.status = response.status;
          throw error;
        }
        return commitUiState(data.ui_state);
      } catch (error) {
        lastError = error;
        if (error?.status && error.status < 500) throw error;
        if (attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, 150));
        }
      }
    }
    throw lastError || new Error("study state failed");
  }, [commitUiState, room, session]);

  const submitRecord = useCallback(async ({
    record: finalRecord,
    finalEdits = {},
    telemetryCheckpoint = null,
    telemetryFlushStatus = "checkpoint_unavailable",
  }) => {
    const response = await fetch(`/api/study/submit/${encodeURIComponent(room)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        room_id: room,
        session,
        record: finalRecord,
        final_edits: finalEdits,
        telemetry_checkpoint: telemetryCheckpoint,
        telemetry_flush_status: telemetryFlushStatus,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.error || `study submission failed: ${response.status}`);
      error.status = response.status;
      error.details = data;
      throw error;
    }
    commitUiState({
      final_record: finalRecord,
      submitted: true,
    });
    return data;
  }, [commitUiState, room, session]);

  const loadRecallState = useCallback(async () => {
    const response = await fetch(
      `/api/study/recall/${encodeURIComponent(room)}?session=${encodeURIComponent(session)}`
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `recall state failed: ${response.status}`);
    return data;
  }, [room, session]);

  const saveRecallResponse = useCallback(async (responsePayload) => {
    const response = await fetch(
      `/api/study/recall/${encodeURIComponent(room)}/response`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session,
          ...responsePayload,
        }),
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `recall response failed: ${response.status}`);
    return data;
  }, [room, session]);

  const completeRecall = useCallback(async () => {
    const response = await fetch(
      `/api/study/recall/${encodeURIComponent(room)}/complete`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session }),
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `recall completion failed: ${response.status}`);
    return data;
  }, [room, session]);

  return useMemo(() => ({
    items,
    record,
    recordVersions,
    setRecord: setBaseRecord,
    uiState,
    connected,
    status,
    error,
    agentBusy,
    turnError,
    complete,
    setComplete,
    sendMessage,
    sendDecision,
    postTelemetry,
    saveUiState,
    submitRecord,
    loadRecallState,
    saveRecallResponse,
    completeRecall,
  }), [
    complete,
    connected,
    error,
    agentBusy,
    items,
    loadRecallState,
    postTelemetry,
    record,
    recordVersions,
    saveUiState,
    saveRecallResponse,
    sendDecision,
    sendMessage,
    status,
    submitRecord,
    completeRecall,
    turnError,
    uiState,
  ]);
}

export function createClientTurnId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `turn-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function buildParticipantMessagePayload(room, text, options = {}) {
  return {
    room,
    text,
    client_turn_id: options.clientTurnId
      || options.client_turn_id
      || createClientTurnId(),
    input_mode: options.inputMode || options.input_mode || "typed",
  };
}
